import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { 
  Info, 
  BarChart3, 
  ChevronLeft, 
  ChevronRight, 
  Sparkles, 
  CheckCircle2, 
  Users, 
  Download, 
  MapPin, 
  CalendarDays, 
  Gem,
  Trophy,
  FileText,
  Copy
} from 'lucide-react';

import Button from '../common/design-system/Button';
import { Card, CardContent } from '../common/design-system/Card';
import { Badge } from '../common/design-system/Badge';
import RichTextDisplay, { TermsDisplay } from '../common/RichTextDisplay';
import {
  downloadCollabReport,
  getCollabSummary,
  getRecipientInvitations,
  publishCollabOffer,
  revokeRecipientInvitation,
  sendRecipientInvites,
} from '../../services/api';
import BusinessMetricsDashboard from './BusinessMetricsDashboard';
import PaidCollabPaymentStatus from '../payments/PaidCollabPaymentStatus';
import styles from './OfferSummary.module.scss';
import clsx from 'clsx';
import ImageWithFallback from '../common/ImageWithFallback';

const formatOfferMoney = (amount, currency = 'USD') => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency,
  currencyDisplay: 'code',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(amount || 0));

// Choice Mapping
const OfferSummary = ({ isPreview = false }) => {
  const { t } = useTranslation('collab');
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [exportingFormat, setExportingFormat] = useState(null);
  const [inviteEmails, setInviteEmails] = useState('');
  const [recipientInvites, setRecipientInvites] = useState([]);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab === 'performance' || tab === 'metrics') {
      setActiveTab('metrics');
    }
  }, [location.search]);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true);
        if (isPreview) {
          const previewData = JSON.parse(localStorage.getItem('offerPreview'));
          if (previewData) setOffer(previewData);
          else navigate('/business/create-collab');
        } else {
          const summaryResp = await getCollabSummary(id);
          setOffer(summaryResp.data);
        }
      } catch (error) {
        console.error('Error fetching collab data:', error);
        if (!silent) toast.error(t('summary.messages.load_error'));
      } finally {
        if (!silent) setLoading(false);
      }
  }, [id, isPreview, navigate, t]);

  useEffect(() => {
    fetchData();
    if (isPreview) return undefined;

    const interval = window.setInterval(() => {
      fetchData({ silent: true });
    }, 30000);

    return () => window.clearInterval(interval);
  }, [fetchData, isPreview]);

  useEffect(() => {
    if (isPreview) return;
    let isMounted = true;
    const fetchInvitations = async () => {
      try {
        const response = await getRecipientInvitations(id);
        if (isMounted) setRecipientInvites(response.data);
      } catch (error) {
        if (isMounted) setRecipientInvites([]);
      }
    };
    fetchInvitations();
    const interval = window.setInterval(fetchInvitations, 30000);
    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, [id, isPreview]);

  const isPaidCollab = ['paid', 'hybrid'].includes(offer?.compensation_mode) || Boolean(offer?.sponsor_this_offer);
  const paidPaymentConfirmed = offer?.paid_collab_payment_status === 'paid';
  const capacityReached = Boolean(
    offer && Number(offer.quantity || 0) > 0
      && Number(offer.active_collabs_count || 0) >= Number(offer.quantity)
  );
  const invitationsOpen = Boolean(
    offer && (
      !capacityReached && (
        (!isPaidCollab && offer.is_feed_visible)
        || (isPaidCollab && offer.is_feed_visible && paidPaymentConfirmed)
      )
    )
  );

  const handlePublishPaidCollab = async () => {
    if (publishing) return;
    try {
      setPublishing(true);
      await publishCollabOffer(id);
      toast.success(t('summary.actions.publish_success'));
      await fetchData({ silent: true });
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === 'paid_collab_payment_required') {
        toast.info(t('payment_release.publish_blocked'));
      } else if (error.response?.status === 402 && !code) {
        navigate(`/business/payment/required?offerId=${id}`);
      } else {
        toast.error(error.response?.data?.error || t('summary.actions.publish_error'));
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleSendInvites = async () => {
    const rawEmails = inviteEmails
      .split(/[\n,]/)
      .map((email) => email.trim())
      .filter(Boolean);
    const emails = [...new Set(rawEmails.map((email) => email.toLowerCase()))];
    const invalidEmails = emails.filter((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmails.length) {
      toast.error(t('summary.messages.private_invites_invalid'));
      return;
    }
    if (emails.length > 50) {
      toast.error(t('summary.messages.private_invites_limit'));
      return;
    }
    if (!emails.length) return;
    try {
      setSendingInvites(true);
      const response = await sendRecipientInvites(id, emails);
      setRecipientInvites((current) => {
        const incomingIds = new Set(response.data.invitations.map((invite) => invite.id));
        return [...response.data.invitations, ...current.filter((invite) => !incomingIds.has(invite.id))];
      });
      setInviteEmails('');
      if (response.data.failed?.length) {
        toast.warn(t('summary.messages.private_invites_partial'));
      } else {
        toast.success(t('summary.messages.private_invites_sent'));
      }
    } catch (error) {
      toast.error(t('summary.messages.private_invites_error'));
    } finally {
      setSendingInvites(false);
    }
  };

  const handleRevokeInvite = async (invitationId) => {
    if (!window.confirm(t('summary.private_invites.revoke_confirm'))) return;
    try {
      const response = await revokeRecipientInvitation(id, invitationId);
      setRecipientInvites((current) =>
        current.map((invite) => invite.id === invitationId ? response.data : invite)
      );
    } catch (error) {
      toast.error(t('summary.messages.private_invite_revoke_error'));
    }
  };

  const getFilenameFromHeaders = (headers, fallback) => {
    const disposition = headers?.['content-disposition'];
    const match = disposition?.match(/filename="?([^"]+)"?/);
    return match?.[1] || fallback;
  };

  const handleDownloadReport = async (format) => {
    try {
      setExportingFormat(format);
      const response = await downloadCollabReport(id, format);
      const extension = format === 'pdf' ? 'pdf' : format === 'xlsx' ? 'xlsx' : 'csv';
      const filename = getFilenameFromHeaders(response.headers, `foth-${offer.title || id}-performance.${extension}`);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(t('summary.messages.report_downloaded'));
    } catch (error) {
      console.error('Error downloading collab report:', error);
      toast.error(t('summary.messages.report_error'));
    } finally {
      setExportingFormat(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const lang = localStorage.getItem('i18nextLng') || 'es';
    const localeMap = { 'en': 'en-US', 'es': 'es-ES', 'pt': 'pt-BR' };
    return new Date(dateString).toLocaleDateString(localeMap[lang] || 'es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleEdit = () => {
    const isPaidAndFunded = isPaidCollab && paidPaymentConfirmed;
    if (offer.can_edit === false || isPaidAndFunded) {
      toast.info(
        isPaidAndFunded
          ? t('review.edit_locked_paid', 'Las colaboraciones pagadas no pueden editarse una vez financiadas.')
          : t('list.card.edit_locked', 'Esta colaboración no se puede editar porque ya fue financiada o tiene entregas en curso.')
      );
      return;
    }
    navigate(`/business/collabs/${id}/edit`);
  };

  const getAudienceLabel = () => {
    if (!offer.followers_requirements) return t('summary.sections.no_minimum', 'No minimum audience');
    const translated = t(`requirements.${offer.followers_requirements}`, { defaultValue: '' });
    return translated || offer.followers_requirements;
  };

  const getLifecycleBadgeVariant = () => {
    if (offer.lifecycle_status === 'completed') return 'success';
    if (offer.lifecycle_status === 'active') return 'success';
    if (offer.lifecycle_status === 'in_execution') return 'progress';
    if (offer.lifecycle_status === 'ready_to_close') return 'warning';
    if (offer.lifecycle_status === 'window_closed') return 'completed';
    return offer.status === 'active' ? 'success' : 'draft';
  };

  const isFullyCompleted = offer?.lifecycle_status === 'completed' || (
    Number(offer?.quantity || 0) > 0 &&
    Number(offer?.completed_collabs_count || 0) >= Number(offer?.quantity)
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
       <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-gold"></div>
    </div>
  );

  if (!offer) return null;

  return (
    <div className={styles['summary-page']}>
      <div className={styles.container}>
        {/* Workspace Header */}
        <section className={styles['workspace-header']}>
          <div className={styles['header-content']}>
            <div className={styles['header-top']}>
              <div className={styles['workspace-badge']}>
                <Sparkles size={14} /> {t('summary.badge')}
              </div>
              <button 
                className={styles['back-breadcrumb']}
                onClick={() => navigate(isPreview ? '/business/create-collab' : '/business/collabs')}
              >
                <ChevronLeft size={16} /> {t('summary.back')}
              </button>
            </div>

            <div className={styles['header-main-grid']}>
              <div className={styles['title-container']}>
                <div className={styles['title-main']}>
                   <div className={styles['status-row']}>
                    <Badge variant={getLifecycleBadgeVariant()}>
                      {isPreview ? t('summary.preview_badge') : (offer.lifecycle_status ? t(`lifecycle.${offer.lifecycle_status}`, offer.lifecycle_status_display) : offer.status_display)}
                    </Badge>
                    <span className={styles['category-label']}>
                     {isPreview ? t(`types.${offer.type}`) : offer.type_display}
                    </span>
                  </div>
                  {!isPreview && offer.status === 'active' && offer.is_feed_visible === false && (
                    <div className={styles['feed-hidden-banner']}>
                      {t('summary.lifecycle.feed_hidden_banner')}
                    </div>
                  )}
                  <h1>{offer.title || t('summary.default_title')}</h1>
                  <p className={styles['header-summary']}>
                    {offer.summary || offer.description?.replace(/<[^>]*>/g, '').substring(0, 120) || t('summary.default_summary')}
                  </p>
                </div>

                 {!isPreview && (
                  <div className={styles['header-actions']}>
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => navigate(`/business/collabs/${id}/applications`)}
                    >
                      <Users size={16} /> {t('summary.actions.view_candidates')}
                    </Button>
                    <Button
                      variant="primary"
                      size="md"
                      aria-disabled={offer.can_edit === false || (isPaidCollab && paidPaymentConfirmed)}
                      title={offer.can_edit === false || (isPaidCollab && paidPaymentConfirmed) ? (isPaidCollab && paidPaymentConfirmed ? t('review.edit_locked_paid', 'Las colaboraciones pagadas no pueden editarse una vez financiadas.') : t('list.card.edit_locked')) : ''}
                      onClick={handleEdit}
                    >
                      {t('summary.actions.edit')}
                    </Button>
                    {isPaidCollab && offer.status !== 'active' && (
                      <Button
                        variant="primary"
                        size="md"
                        disabled={publishing || !paidPaymentConfirmed}
                        title={!paidPaymentConfirmed ? t('payment_release.publish_blocked') : ''}
                        onClick={handlePublishPaidCollab}
                      >
                        {publishing ? t('summary.actions.publishing') : t('summary.actions.publish')}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className={styles['header-visual-panel']}>
                <div className={styles['visual-frame']}>
                  <ImageWithFallback
                    src={offer.featured_image_url || offer.featured_image}
                    alt={offer.title}
                    name={offer.title}
                  />
                  <div className={styles['visual-overlay']} />
                </div>
              </div>
            </div>

            {/* Header Stats Bridge */}
             <div className={styles['header-stats-row']}>
              <div className={styles['h-stat-item']}>
                <div className={styles.label}>{t('summary.stats.applications')}</div>
                <div className={styles.value}>{offer.applications_count || 0}</div>
              </div>
              <div className={styles['h-stat-item']}>
                <div className={styles.label}>{t('summary.stats.viral_reach')}</div>
                <div className={styles.value}>{Number(offer.viral_reach || 0).toLocaleString()}</div>
              </div>
              <div className={styles['h-stat-item']}>
                <div className={styles.label}>{t('summary.stats.in_execution')}</div>
                <div className={styles.value}>{offer.active_collabs_count || 0}</div>
              </div>
              {offer.pending_deliveries_count > 0 && (
                <button
                  type="button"
                  className={styles['delivery-alert']}
                  onClick={() => setActiveTab('metrics')}
                >
                  {t(`summary.stats.${offer.paid_collab_payment_status && offer.paid_collab_payment_status !== 'paid'
                    ? (offer.pending_deliveries_count === 1 ? 'delivery_payment_pending_one' : 'delivery_payment_pending_many')
                    : (offer.pending_deliveries_count === 1 ? 'new_delivery_one' : 'new_delivery_many')}`, {
                    count: offer.pending_deliveries_count,
                  })}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* 100% Completed Banner */}
        {!isPreview && isFullyCompleted && (
          <section className={styles['completed-hero-banner']}>
            <div className={styles['completed-banner-glow']} />
            <div className={styles['completed-banner-content']}>
              <div className={styles['completed-banner-left']}>
                <div className={styles['completed-icon-badge']}>
                  <Trophy size={28} />
                </div>
                <div>
                  <div className={styles['completed-tag']}>
                    <Sparkles size={13} /> {t('summary.completion_banner.badge')}
                  </div>
                  <h2>{t('summary.completion_banner.title')}</h2>
                  <p>{t('summary.completion_banner.subtitle')}</p>
                </div>
              </div>
              <div className={styles['completed-stats-row']}>
                <div className={styles['completed-stat-box']}>
                  <strong>{offer.completed_collabs_count || offer.quantity} / {offer.quantity}</strong>
                  <span>{t('summary.completion_banner.creators_stat')}</span>
                </div>
                <div className={styles['completed-stat-box']}>
                  <strong>{offer.delivered_posts_count || offer.quantity}</strong>
                  <span>{t('summary.completion_banner.deliverables_stat')}</span>
                </div>
                {isPaidCollab && (
                  <div className={styles['completed-stat-box']}>
                    <strong>${offer.total_budget || (offer.sponsor_amount * offer.quantity)} {offer.cash_currency || 'USD'}</strong>
                    <span>{t('summary.completion_banner.funded_stat')}</span>
                  </div>
                )}
              </div>
              <div className={styles['completed-banner-actions']}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadReport('pdf')}
                  disabled={exportingFormat === 'pdf'}
                >
                  <FileText size={15} />
                  {exportingFormat === 'pdf' ? t('summary.performance.downloading') : t('summary.completion_banner.actions.download_report')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setActiveTab('metrics')}
                >
                  <BarChart3 size={15} />
                  {t('summary.completion_banner.actions.view_metrics')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/business/create-collab')}
                >
                  <Copy size={15} />
                  {t('summary.completion_banner.actions.duplicate_collab')}
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Content Tabs Navigation */}
        <nav className={styles['tabs-nav']}>
          {['details', 'metrics'].map((tab) => {
            return (
               <button 
                key={tab}
                className={clsx(activeTab === tab && styles.active)} 
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'details' && <><Info size={16} /> {t('summary.tabs.details')}</>}
                {tab === 'metrics' && <><BarChart3 size={16} /> {t('summary.tabs.metrics')}</>}
              </button>
            );
          })}
        </nav>

        <div className={styles['content-layout']}>
          <div className={styles['main-content-area']}>
            <AnimatePresence mode="wait">
              {activeTab === 'details' && (
                <motion.div 
                  key="details"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={styles['tab-pane']}
                >
                  {!isPreview && isPaidCollab && (
                    <PaidCollabPaymentStatus collabId={id} role="business" collab={offer} />
                  )}
                  <Card className={styles['content-card']}>
                    <CardContent className="p-8">
                       <div className={styles['section-header']}>
                        <div className={styles['section-icon']}><Info size={18} /></div>
                        <h3>{t('summary.sections.description')}</h3>
                      </div>
                      <RichTextDisplay content={offer.description} />
                    </CardContent>
                  </Card>

                  {offer.additional_images?.length > 0 && (
                    <Card className={clsx(styles['content-card'], "mt-6")}>
                      <CardContent className="p-8">
                         <div className={styles['section-header']}>
                          <div className={styles['section-icon']}><Sparkles size={18} /></div>
                          <h3>{t('summary.sections.gallery')}</h3>
                        </div>
                        <div className={styles['gallery-wrapper']}>
                          <ImageWithFallback
                            src={typeof offer.additional_images[currentImageIndex] === 'string'
                              ? offer.additional_images[currentImageIndex]
                              : (offer.additional_images[currentImageIndex]?.image || offer.additional_images[currentImageIndex]?.file)}
                            alt={`${offer.title} gallery`}
                            name={offer.title}
                            className={styles['gallery-img']}
                          />
                          {offer.additional_images.length > 1 && (
                            <div className={styles['gallery-nav']}>
                              <button 
                                onClick={() => setCurrentImageIndex(prev => prev === 0 ? offer.additional_images.length - 1 : prev - 1)}
                              >
                                <ChevronLeft size={20} />
                              </button>
                              <button 
                                onClick={() => setCurrentImageIndex(prev => prev === offer.additional_images.length - 1 ? 0 : prev + 1)}
                              >
                                <ChevronRight size={20} />
                              </button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <Card className={clsx(styles['content-card'], "mt-6")}>
                    <CardContent className="p-8">
                       <div className={styles['section-header']}>
                        <div className={styles['section-icon']}><CheckCircle2 size={18} /></div>
                        <h3>{t('summary.sections.terms')}</h3>
                      </div>
                      <TermsDisplay content={offer.terms_and_conditions} />
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {activeTab === 'metrics' && (
                <motion.div 
                  key="metrics" 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }}
                  className={styles['tab-pane']}
                >
                  <div className={styles['performance-toolbar']}>
                    <div>
                      <h2>{t('summary.performance.title')}</h2>
                      <p>{t('summary.performance.subtitle')}</p>
                    </div>
                    <div className={styles['report-actions']}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/business/collabs/${id}/report`)}
                      >
                        <BarChart3 size={16} /> Creator EMV Report
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadReport('xlsx')}
                        disabled={Boolean(exportingFormat)}
                      >
                        <Download size={16} /> {exportingFormat === 'xlsx' ? t('summary.performance.downloading') : 'Excel'}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleDownloadReport('pdf')}
                        disabled={Boolean(exportingFormat)}
                      >
                        <Download size={16} /> {exportingFormat === 'pdf' ? t('summary.performance.downloading') : t('summary.performance.pdf')}
                      </Button>
                    </div>
                  </div>
                  <BusinessMetricsDashboard offerId={id} title={offer.title} offer={offer} onApplicationCompleted={() => fetchData({ silent: true })} />
                </motion.div>
              )}

            </AnimatePresence>
          </div>

          <aside className={styles['sidebar']}>
            {/* PRIVATE INVITATIONS */}
            {!isPreview && (
               <div className={styles['viral-card-v2']}>
                <div className={styles['card-glow']} />
                <div className={styles['card-header']}>
                  <div className={styles['viral-badge']}><Sparkles size={14} /> {t('summary.private_invites.badge')}</div>
                  <h3>{t('summary.private_invites.title')}</h3>
                  <p>{t('summary.private_invites.description')}</p>
                </div>

                {isPaidCollab && invitationsOpen && (
                  <div className={styles['invite-closed-note']}>
                    {t('summary.private_invites.paid_flow_help')}
                  </div>
                )}
                {!invitationsOpen && (
                  <div className={styles['invite-closed-note']}>
                    {capacityReached
                      ? t('summary.lifecycle.capacity_reached_note')
                      : t('summary.lifecycle.invite_closed_note')}
                  </div>
                )}

                <textarea
                  value={inviteEmails}
                  onChange={(event) => setInviteEmails(event.target.value)}
                  placeholder={'ana@email.com\nleo@email.com'}
                  rows={4}
                  disabled={!invitationsOpen}
                  className={styles['invite-textarea']}
                />
                <Button variant="primary" fullWidth onClick={handleSendInvites} disabled={sendingInvites || !invitationsOpen}>
                  {sendingInvites ? t('summary.private_invites.sending') : t('summary.private_invites.send')}
                </Button>

                {recipientInvites.length > 0 && (
                  <div className={styles['invite-list']}>
                    {recipientInvites.map((invite) => (
                      <div key={invite.id} className={styles['invite-row']}>
                        <div className={styles['invite-meta']}>
                          <div className={styles['invite-email']}>{invite.email}</div>
                            <small className={styles['invite-status']}>
                              {t(`summary.private_invites.status.${invite.status}`, { defaultValue: invite.status })}
                              {invite.delivery_status && (
                                <> · {t(`summary.private_invites.delivery_status.${invite.delivery_status}`, { defaultValue: invite.delivery_status })}</>
                              )}
                            </small>
                        </div>
                        {!['accepted', 'declined', 'revoked', 'expired'].includes(invite.status) && (
                          <button
                            type="button"
                            className={styles['invite-revoke-button']}
                            onClick={() => handleRevokeInvite(invite.id)}
                          >
                            {t('summary.private_invites.revoke')}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className={styles['discovery-nudge']} style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                   <Button 
                    variant="primary" 
                    fullWidth 
                    onClick={() => navigate(`/business/creators/discovery?offerId=${id}`)}
                    className={styles['find-btn']}
                    disabled={!invitationsOpen}
                  >
                    <Users size={16} style={{ marginRight: '8px' }} /> {t('summary.sections.find_creators')}
                  </Button>
                  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '8px', textAlign: 'center' }}>
                    {t('summary.sections.find_help')}
                  </p>
                </div>
              </div>
            )}

            {/* COLLAB INFO SIDEBAR */}
             <div className={styles['info-sidebar-card']}>
              <div className={styles['info-row']}>
                <span className={styles.label}><MapPin size={14} /> {t('summary.sections.location')}</span>
                <span className={styles.value}>{offer.country_name || 'Global'}</span>
              </div>
               <div className={styles['info-row']}>
                <span className={styles.label}><CalendarDays size={14} /> {t('summary.sections.schedule')}</span>
                <span className={styles.value}>{formatDate(offer.from_date)} - {formatDate(offer.to_date)}</span>
              </div>
               <div className={styles['info-row']}>
                <span className={styles.label}><Users size={14} /> {t('summary.sections.audience')}</span>
                <span className={styles.value}>{getAudienceLabel()}</span>
              </div>
              {isPaidCollab && (
                <div className={styles['info-row']}>
                  <span className={styles.label}><Users size={14} /> {t('summary.sections.creators_count', 'Creadores')}</span>
                  <span className={styles.value}>{offer.quantity || 1}</span>
                </div>
              )}

               <div className={styles['investment-footer']}>
                <div className={styles.label}><Gem size={14} /> {t('summary.sections.budget_per_creator')}</div>
                <div className={styles.value}>
                  {offer.sponsor_this_offer || ['paid', 'hybrid'].includes(offer.compensation_mode)
                    ? formatOfferMoney(offer.cash_compensation ?? offer.sponsor_amount, offer.cash_currency || 'USD')
                    : `$${Number(offer.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </div>
                {isPaidCollab && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed var(--border-subtle, rgba(0, 0, 0, 0.1))' }}>
                    <div className={styles.label}><Sparkles size={14} /> {t('summary.sections.total_investment', 'Inversión Total de la Campaña')}</div>
                    <div className={styles.value} style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--brand-gold, #c9a227)' }}>
                      {formatOfferMoney(
                        offer.total_budget ?? ((Number(offer.cash_compensation ?? offer.sponsor_amount ?? 0)) * Number(offer.quantity || 1)),
                        offer.cash_currency || 'USD'
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {isPreview && (
               <div className="mt-8 flex flex-col gap-4">
                <Button variant="primary" size="lg" className="w-full" onClick={() => navigate(`/business/collabs/${id}/edit`)}>
                  {t('summary.actions.config_payment')}
                </Button>
                <Button variant="outline" size="lg" className="w-full" onClick={() => navigate('/business/create-collab')}>
                  {t('summary.actions.keep_editing')}
                </Button>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default OfferSummary;
